import { useEffect, useMemo, useRef, useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import { AnimatePresence, motion } from "framer-motion";
import styles from "./style.module.css";
import { ArrowDownIcon } from "../../../../../utils/Icons";

/**
 * Props:
 * - options: Array<{ value: string, label: string, email?: string, sublabel?: string }>
 * - selected: string | string[]
 * - onChange(newValue)
 * - placeholder: string
 * - multiple?: boolean
 * - disabled?: boolean
 * - searchPlaceholder?: string
 * - maxSummary?: number   (for multiple summary)
 * - renderOption?: (opt) => ReactNode  (custom content)
 */
export default function SelectDropdown({
    options = [],
    selected,
    onChange,
    placeholder = "Select…",
    multiple = false,
    disabled = false,
    searchPlaceholder = "Search…",
    maxSummary = 3,
    renderOption,
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const isSelected = (val) =>
        multiple ? Array.isArray(selected) && selected.includes(val) : selected === val;

    const applySelect = (val) => {
        if (multiple) {
            const set = new Set(Array.isArray(selected) ? selected : []);
            set.has(val) ? set.delete(val) : set.add(val);
            onChange([...set]);
        } else {
            onChange(val === selected ? "" : val);
            setOpen(false);
        }
    };

    const filtered = useMemo(
        () => options.filter((o) => (o.label || "").toLowerCase().includes(search.toLowerCase())),
        [options, search]
    );

    const headerLabel = useMemo(() => {
        if (multiple) {
            const vals = Array.isArray(selected) ? selected : [];
            if (!vals.length) return <p style={{ color: "#c7c7c7" }}>{placeholder}</p>;
            const labels = options
                .filter((o) => vals.includes(o.value))
                .map((o) => o.label);
            const shown = labels.slice(0, maxSummary).join(", ");
            const extra = labels.length > maxSummary ? ` +${labels.length - maxSummary}` : "";
            return `${shown}${extra}`;
        } else {
            const found = options.find((o) => o.value === selected);
            return found ? found.label : <p style={{ color: "#c7c7c7" }}>{placeholder}</p>;
        }
    }, [multiple, selected, options, placeholder, maxSummary]);

    return (
        <div className={styles.dropDown} ref={ref} aria-disabled={disabled}>
            <div
                className={styles.SelectinputField}
                onClick={() => !disabled && setOpen((o) => !o)}
                style={disabled ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
            >
                <p>{headerLabel}</p>
                <ArrowDownIcon height={10} fill="#989898" style={{ transform: open ? "rotate(180deg)" : undefined }} />
            </div>

            <AnimatePresence>
                {open && !disabled && (
                    <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.15 }}
                        className={styles.dropDownBody}
                    >
                        <div className={styles.dropDownBodyInner}>
                            {/* <div className={styles.stickyCon}>
                                <div className={styles.searcCon}>
                                    <input
                                        placeholder={searchPlaceholder}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div> */}

                            {filtered.map((opt, idx) => {
                                const active = isSelected(opt.value);
                                return (
                                    <div
                                        key={opt.value ?? idx}
                                        className={`${styles.dropDownElem} ${active ? styles.activeDropElem : ""}`}
                                        onClick={() => applySelect(opt.value)}
                                    >
                                        <Checkbox
                                            checked={active}
                                            onChange={() => applySelect(opt.value)}
                                            sx={{
                                                color: "var(--primary-color)",
                                                padding: "5px",
                                                "&.Mui-checked": { color: "var(--primary-color)" },
                                                "& .MuiSvgIcon-root": { fontSize: 18 },
                                            }}
                                        />
                                        <label>
                                            {renderOption ? (
                                                renderOption(opt)
                                            ) : (
                                                <h5>
                                                    {opt.label}
                                                    {opt.sublabel && (
                                                        <span style={{ color: "#9aa0a6", fontWeight: 600, marginLeft: 6 }}>
                                                            {opt.sublabel}
                                                        </span>
                                                    )}
                                                </h5>
                                            )}
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
